import React from 'react';
import { getIntl } from '../../../utils/intl';
import { APITypes, MedicalDataTypes } from '../../../primary-record-types';
import * as CardV2 from '../../v2BaseComponents/Card';
import getDateString from '../../../utils/getDateString';
import { allergyTypeMessages, conditionStatusMessages, reactionTypeMessages } from '../../../utils/sharedMessages';
import { resourceTypeGuards } from '../../../utils/medicalDataUtils';
import useResource from '../../../hooks/api/useResource';
import LazyLoadedReferenceCounts from './LazyLoadedReferenceCounts';
import { Checkbox } from '../../v2BaseComponents/Inputs';
import VisitCard from './VisitCard';
import Stack from '../../v2BaseComponents/Stack';
import { isPresent } from 'ts-is-present';
import { useResourceTitle } from '../../../utils/resourceUtils/getResourceTitle';
import { useIsFocused } from '@react-navigation/native';
import { StyleSheet, View } from 'react-native';
import { getDosageInstructions } from '../../../utils/getDosageInstructions';

const intl = getIntl('resource-card', {
    startDate: 'Start Date',
    dateAdded: 'Date added',
    date: 'Date',
    status: 'Status',
    source: 'Source',
    ehrIntegration: 'Patient Portal',
    primaryRecord: 'Primary Record',
    resources: 'List',
    notes: 'Notes',
    active: 'Active',
    inactive: 'Inactive',
    'as-needed': 'As Needed',
});

export type ResourceCardOwnProps = {
    resource: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordResource>;
    category: MedicalDataTypes.PrimaryRecordResourceCategory;
    profileId: string;
    onPress?: () => void;
    isSelectable?: boolean;
    isSelected?: boolean;
    isDisabled?: boolean;
    flat?: boolean;
};

const _styles = StyleSheet.create({
    leftContent: {
        backgroundColor: 'red',
    },
});

export type ResourceCardProps = ResourceCardOwnProps;
const ResourceCard: React.FC<ResourceCardProps> = ({
    resource,
    profileId,
    onPress,
    isSelectable,
    isSelected,
    isDisabled,
    flat,
}) => {
    const isFocused = useIsFocused();
    const resourceTitle = useResourceTitle(resource, profileId);

    if (!resource || !isFocused) return null;

    const maybeRenderCheckbox = (content: React.ReactNode) =>
        isSelectable ? (
            <CardV2.CardLeftContent
                style={{ display: 'flex' }}
                leftContent={
                    <View style={{ height: '100%' }}>
                        <Stack vertical padding={4} justify={'center'} align={'center'} flex={1}>
                            <Checkbox
                                value={!!isSelected}
                                onChange={() => onPress?.()}
                                title=""
                                isDisabled={isDisabled}
                            />
                        </Stack>
                    </View>
                }
            >
                {content}
            </CardV2.CardLeftContent>
        ) : (
            content
        );

    const renderCountsWithSubtext = (subtext: React.ReactNode) => (
        <CardV2.CardRightContent
            rightContent={
                <Stack vertical justify="flex-end" align="flex-end" style={{ alignSelf: 'stretch', minWidth: 1 }}>
                    <LazyLoadedReferenceCounts resource={resource} medicalProfileId={profileId} />
                </Stack>
            }
        >
            {subtext}
        </CardV2.CardRightContent>
    );

    const getDateSubtext = (
        resource: APITypes.medicalDataService.ListResourceResponseItem<
            MedicalDataTypes.PrimaryRecordVaccination | MedicalDataTypes.PrimaryRecordProcedure
        >,
    ) => {
        if (resource.dateTime) return `${intl('date')}: ${getDateString(resource.dateTime, 'date')}`;
        else if (resource.dateAdded) return `${intl('dateAdded')}: ${getDateString(resource.dateAdded, 'date')}`;
        return null;
    };

    const renderConditionCard = (
        resource: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordCondition>,
    ) => {
        return (
            <CardV2.Card
                key={resource.id}
                onPress={onPress}
                isDisabled={isDisabled}
                testID="resource-card-condition"
                flat={flat}
            >
                {maybeRenderCheckbox(
                    <CardV2.CardContent>
                        <CardV2.CardHeader>{resourceTitle}</CardV2.CardHeader>
                        <CardV2.CardInlineContent>
                            <CardV2.CardChipSet
                                chips={[
                                    {
                                        title: conditionStatusMessages(resource.status),
                                    },
                                ]}
                            />
                            <CardV2.CardBody2>
                                {resource.startDateTime
                                    ? `${intl('startDate')}: ${getDateString(resource.startDateTime, 'date')}`
                                    : `${intl('dateAdded')}: ${getDateString(resource.dateAdded, 'date')}`}
                            </CardV2.CardBody2>
                        </CardV2.CardInlineContent>
                        {renderCountsWithSubtext(<CardV2.CardBody2>{resource.notes}</CardV2.CardBody2>)}
                    </CardV2.CardContent>,
                )}
            </CardV2.Card>
        );
    };

    const renderAllergyCard = (
        resource: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordAllergy>,
    ) => {
        return (
            <CardV2.Card
                key={resource.id}
                onPress={onPress}
                isDisabled={isDisabled}
                testID="resource-card-allergy"
                flat={flat}
            >
                {maybeRenderCheckbox(
                    <CardV2.CardContent>
                        <CardV2.CardHeader>{resourceTitle}</CardV2.CardHeader>
                        <CardV2.CardBody2>
                            {resource.startDateTime
                                ? `${intl('startDate')}: ${getDateString(resource.startDateTime, 'date')}`
                                : `${intl('dateAdded')}: ${getDateString(resource.dateAdded, 'date')}`}
                        </CardV2.CardBody2>
                        <CardV2.CardChipSet
                            chips={[
                                resource.allergyType
                                    ? {
                                          title: allergyTypeMessages(resource.allergyType),
                                      }
                                    : null,
                                resource.reactionType
                                    ? {
                                          title: reactionTypeMessages(resource.reactionType),
                                      }
                                    : null,
                            ].filter(isPresent)}
                        />
                        {renderCountsWithSubtext(<CardV2.CardBody2>{resource.notes}</CardV2.CardBody2>)}
                    </CardV2.CardContent>,
                )}
            </CardV2.Card>
        );
    };

    const renderMedicationCard = (
        resource: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordMedication>,
    ) => {
        return (
            <CardV2.Card
                key={resource.id}
                onPress={onPress}
                isDisabled={isDisabled}
                testID="resource-card-medication"
                flat={flat}
            >
                {maybeRenderCheckbox(
                    <CardV2.CardContent>
                        <CardV2.CardHeader>{resourceTitle}</CardV2.CardHeader>
                        <CardV2.CardChipSet
                            chips={[
                                { title: intl(resource.isActive ? 'active' : 'inactive') },
                                resource.asNeeded
                                    ? {
                                          title: intl('as-needed'),
                                          highlight: true,
                                      }
                                    : null,
                            ].filter(isPresent)}
                        />
                        <CardV2.CardBody2>{getDosageInstructions(resource)}</CardV2.CardBody2>
                        {renderCountsWithSubtext(<CardV2.CardBody2>{resource.notes}</CardV2.CardBody2>)}
                    </CardV2.CardContent>,
                )}
            </CardV2.Card>
        );
    };

    const renderImagingCard = (
        resource: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordImaging>,
    ) => {
        return (
            <CardV2.Card
                key={resource.id}
                onPress={onPress}
                isDisabled={isDisabled}
                testID="resource-card-imaging"
                flat={flat}
            >
                {maybeRenderCheckbox(
                    <CardV2.CardContent>
                        <CardV2.CardHeader>{resourceTitle}</CardV2.CardHeader>
                        {renderCountsWithSubtext(
                            <CardV2.CardBody2>
                                {resource.dateAdded
                                    ? `${intl('dateAdded')}: ${getDateString(resource.dateAdded, 'date')}`
                                    : null}
                            </CardV2.CardBody2>,
                        )}
                    </CardV2.CardContent>,
                )}
            </CardV2.Card>
        );
    };

    const renderVaccinationCard = (
        resource: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordVaccination>,
    ) => {
        return (
            <CardV2.Card
                key={resource.id}
                onPress={onPress}
                isDisabled={isDisabled}
                testID="resource-card-vaccination"
                flat={flat}
            >
                {maybeRenderCheckbox(
                    <CardV2.CardContent>
                        <CardV2.CardHeader>{resourceTitle}</CardV2.CardHeader>
                        {renderCountsWithSubtext(<CardV2.CardBody2>{getDateSubtext(resource)}</CardV2.CardBody2>)}
                    </CardV2.CardContent>,
                )}
            </CardV2.Card>
        );
    };

    const renderProcedureCard = (
        resource: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordProcedure>,
    ) => {
        return (
            <CardV2.Card
                key={resource.id}
                onPress={onPress}
                isDisabled={isDisabled}
                testID="resource-card-procedure"
                flat={flat}
            >
                {maybeRenderCheckbox(
                    <CardV2.CardContent>
                        <CardV2.CardHeader>{resourceTitle}</CardV2.CardHeader>
                        {renderCountsWithSubtext(<CardV2.CardBody2>{getDateSubtext(resource)}</CardV2.CardBody2>)}
                    </CardV2.CardContent>,
                )}
            </CardV2.Card>
        );
    };

    if (resourceTypeGuards.isCondition(resource)) {
        return renderConditionCard(resource);
    } else if (resourceTypeGuards.isAllergy(resource)) {
        return renderAllergyCard(resource);
    } else if (resourceTypeGuards.isMedication(resource)) {
        return renderMedicationCard(resource);
    } else if (resourceTypeGuards.isVisit(resource)) {
        return (
            <VisitCard
                visit={resource}
                profileId={profileId}
                onPress={onPress}
                isDisabled={isDisabled}
                isSelectable={isSelectable}
                isSelected={isSelected}
                flat={flat}
            />
        );
    } else if (resourceTypeGuards.isImaging(resource)) {
        return renderImagingCard(resource);
    } else if (resourceTypeGuards.isVaccination(resource)) {
        return renderVaccinationCard(resource);
    } else if (resourceTypeGuards.isProcedure(resource)) {
        return renderProcedureCard(resource);
    }
};

export default React.memo(ResourceCard);

export type UnloadedResourceCardProps = {
    resourceId: string;
    category: MedicalDataTypes.PrimaryRecordResourceCategory;
    profileId: string;
    onPress?: () => void;
    skeletonFallback?: boolean;
};

export const UnloadedResourceCard: React.FC<UnloadedResourceCardProps> = ({
    resourceId,
    category,
    profileId,
    onPress,
    skeletonFallback,
}) => {
    const { resource, isLoadingResource } = useResource({
        medicalProfileId: profileId,
        resourceId: resourceId,
        resourceCategory: category,
    });

    if (!resource || isLoadingResource) return skeletonFallback ? <CardV2.CardSkeleton /> : null;

    return <ResourceCard resource={resource.resource} category={category} profileId={profileId} onPress={onPress} />;
};
